import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import ConfirmModal from '../components/ConfirmModal';
import useAdminAuth from '../hooks/useAdminAuth';
import { Trophy, Trash2, Ban, Camera, Phone, Mail, Globe, UserX, RefreshCw, Award } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminCompetition = () => {
  const navigate = useNavigate();
  useAdminAuth();
  
  const [photos, setPhotos] = useState([]);
  const [blockedPosters, setBlockedPosters] = useState([]);
  const [photoImages, setPhotoImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [competitionMonth, setCompetitionMonth] = useState('');
  const [totalEntries, setTotalEntries] = useState(0);
  
  // Modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showUnblockModal, setShowUnblockModal] = useState(false);
  const [showDrawWinnerModal, setShowDrawWinnerModal] = useState(false);
  
  const [photoToDelete, setPhotoToDelete] = useState(null);
  const [posterToBlock, setPosterToBlock] = useState(null);
  const [posterToUnblock, setPosterToUnblock] = useState(null);
  
  // Block form
  const [blockForm, setBlockForm] = useState({
    identifier: '',
    identifier_type: 'phone',
    reason: ''
  });

  const adminEmail = localStorage.getItem('admin_email') || 'info@trucknsendit.com';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [photosRes, blockedRes] = await Promise.all([
        axios.get(`${API}/api/admin/competition/photos?admin_email=${adminEmail}`),
        axios.get(`${API}/api/admin/competition/blocked-posters?admin_email=${adminEmail}`)
      ]);
      
      setPhotos(photosRes.data.photos || []);
      setCompetitionMonth(photosRes.data.competition_month || '');
      setTotalEntries(photosRes.data.total_entries || 0);
      setBlockedPosters(blockedRes.data.blocked_posters || []);
      
      // Load images for photos
      for (const photo of photosRes.data.photos || []) {
        loadPhotoImage(photo.id);
      }
    } catch (error) {
      console.error('Error fetching competition data:', error);
      toast.error('Failed to load competition data');
    } finally {
      setLoading(false);
    }
  };

  const loadPhotoImage = async (photoId) => {
    try {
      const res = await axios.get(`${API}/api/competition/photos/${photoId}/image`);
      if (res.data.image) {
        setPhotoImages(prev => ({ ...prev, [photoId]: res.data.image }));
      }
    } catch (error) {
      console.error('Error loading photo image:', error);
    }
  };

  const handleDeletePhoto = async () => {
    if (!photoToDelete) return;
    
    try {
      await axios.delete(`${API}/api/admin/competition/photos/${photoToDelete.id}?admin_email=${adminEmail}`);
      toast.success('Photo deleted successfully');
      setShowDeleteModal(false);
      setPhotoToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete photo');
    }
  };

  const handleBlockPoster = async () => {
    if (!blockForm.identifier) {
      toast.error('Please enter an identifier to block');
      return;
    }
    
    try {
      await axios.post(`${API}/api/admin/competition/block-poster`, null, {
        params: {
          admin_email: adminEmail,
          identifier: blockForm.identifier,
          identifier_type: blockForm.identifier_type,
          reason: blockForm.reason
        }
      });
      toast.success('Poster blocked successfully');
      setShowBlockModal(false);
      setBlockForm({ identifier: '', identifier_type: 'phone', reason: '' });
      setPosterToBlock(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to block poster');
    }
  };

  const handleUnblockPoster = async () => {
    if (!posterToUnblock) return;
    
    try {
      await axios.delete(`${API}/api/admin/competition/block-poster`, {
        params: {
          admin_email: adminEmail,
          identifier: posterToUnblock.identifier,
          identifier_type: posterToUnblock.identifier_type
        }
      });
      toast.success('Poster unblocked successfully');
      setShowUnblockModal(false);
      setPosterToUnblock(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unblock poster');
    }
  };

  const handleDrawWinner = async () => {
    try {
      const res = await axios.post(`${API}/api/admin/competition/draw-winner?admin_email=${adminEmail}`);
      toast.success(res.data.message);
      setShowDrawWinnerModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to draw winner');
    }
  };

  const openBlockModalFromPhoto = (photo) => {
    setPosterToBlock(photo);
    setBlockForm({
      identifier: photo.submitter_phone,
      identifier_type: 'phone',
      reason: ''
    });
    setShowBlockModal(true);
  };

  const getMonthName = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
  };

  const getBlockTypeIcon = (type) => {
    switch (type) {
      case 'phone': return <Phone className="w-4 h-4" />;
      case 'email': return <Mail className="w-4 h-4" />;
      case 'ip': return <Globe className="w-4 h-4" />;
      default: return <Ban className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#1e3a5f] flex items-center gap-2">
              <Trophy className="w-8 h-8 text-yellow-500" />
              Competition Management
            </h1>
            <p className="text-gray-600 mt-1">
              {getMonthName(competitionMonth)} - {totalEntries} entries
            </p>
          </div>
          
          <div className="flex gap-3">
            <Button
              onClick={() => setShowBlockModal(true)}
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50"
              data-testid="block-poster-btn"
            >
              <Ban className="w-4 h-4 mr-2" />
              Block Poster
            </Button>
            <Button
              onClick={() => setShowDrawWinnerModal(true)}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
              data-testid="draw-winner-btn"
            >
              <Award className="w-4 h-4 mr-2" />
              Draw Winner
            </Button>
            <Button
              onClick={fetchData}
              variant="outline"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Blocked Posters Section */}
        {blockedPosters.length > 0 && (
          <Card className="p-4 mb-6 bg-red-50 border-red-200">
            <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
              <UserX className="w-5 h-5" />
              Blocked Posters ({blockedPosters.length})
            </h3>
            <div className="flex flex-wrap gap-2">
              {blockedPosters.map((blocked) => (
                <div
                  key={blocked.id}
                  className="bg-white border border-red-200 rounded-lg px-3 py-2 flex items-center gap-2 text-sm"
                >
                  {getBlockTypeIcon(blocked.identifier_type)}
                  <span className="font-medium">{blocked.identifier}</span>
                  {blocked.reason && (
                    <span className="text-gray-500">- {blocked.reason}</span>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-green-600 hover:bg-green-50 h-6 px-2"
                    onClick={() => {
                      setPosterToUnblock(blocked);
                      setShowUnblockModal(true);
                    }}
                    data-testid={`unblock-${blocked.id}`}
                  >
                    Unblock
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Photos Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p>Loading entries...</p>
          </div>
        ) : photos.length === 0 ? (
          <Card className="p-12 text-center">
            <Camera className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No entries this month</h3>
            <p className="text-gray-500">Competition entries will appear here when submitted.</p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {photos.map((photo, index) => (
              <Card key={photo.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                {/* Rank Badge */}
                {index < 3 && (
                  <div className={`absolute top-3 left-3 z-10 w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                    index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : 'bg-amber-700'
                  }`}>
                    {index + 1}
                  </div>
                )}
                
                {/* Photo */}
                <div className="relative h-48 bg-gray-200">
                  {photoImages[photo.id] ? (
                    <img
                      src={`data:image/jpeg;base64,${photoImages[photo.id]}`}
                      alt={photo.truck_description || 'Truck entry'}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Camera className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                </div>
                
                {/* Details */}
                <div className="p-4">
                  <h3 className="font-semibold text-[#1e3a5f] mb-1">
                    {photo.truck_description || 'Aussie Truck'}
                  </h3>
                  
                  {/* Admin Info */}
                  <div className="text-sm text-gray-600 space-y-1 mb-3">
                    <p><strong>Name:</strong> {photo.submitter_name}</p>
                    <p><strong>Phone:</strong> {photo.submitter_phone}</p>
                    {photo.submitter_email && (
                      <p><strong>Email:</strong> {photo.submitter_email}</p>
                    )}
                    {photo.company_name && (
                      <p><strong>Company:</strong> {photo.company_name}</p>
                    )}
                    <p><strong>Votes:</strong> <span className="font-bold text-blue-600">{photo.votes}</span></p>
                  </div>
                  
                  {/* Admin Actions */}
                  <div className="flex gap-2 pt-2 border-t">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 text-red-600 border-red-300 hover:bg-red-50"
                      onClick={() => {
                        setPhotoToDelete(photo);
                        setShowDeleteModal(true);
                      }}
                      data-testid={`delete-photo-${photo.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 text-orange-600 border-orange-300 hover:bg-orange-50"
                      onClick={() => openBlockModalFromPhoto(photo)}
                      data-testid={`block-poster-${photo.id}`}
                    >
                      <Ban className="w-4 h-4 mr-1" />
                      Block
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Delete Photo Modal */}
        <ConfirmModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setPhotoToDelete(null);
          }}
          onConfirm={handleDeletePhoto}
          title="Delete Photo"
          message={`Are you sure you want to delete this photo submission from "${photoToDelete?.submitter_name}"? This action cannot be undone and will remove all votes for this entry.`}
          confirmText="Delete Photo"
          confirmVariant="destructive"
        />

        {/* Block Poster Modal */}
        {showBlockModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md p-6">
              <h2 className="text-xl font-bold text-[#1e3a5f] mb-4 flex items-center gap-2">
                <Ban className="w-5 h-5 text-red-600" />
                Block Poster
              </h2>
              
              <div className="space-y-4">
                <div>
                  <Label>Block Type</Label>
                  <Select
                    value={blockForm.identifier_type}
                    onValueChange={(value) => setBlockForm(prev => ({ ...prev, identifier_type: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="phone">Phone Number</SelectItem>
                      <SelectItem value="email">Email Address</SelectItem>
                      <SelectItem value="ip">IP Address</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Identifier</Label>
                  <Input
                    value={blockForm.identifier}
                    onChange={(e) => setBlockForm(prev => ({ ...prev, identifier: e.target.value }))}
                    placeholder={
                      blockForm.identifier_type === 'phone' ? '04XX XXX XXX' :
                      blockForm.identifier_type === 'email' ? 'email@example.com' :
                      'IP Address'
                    }
                  />
                </div>
                
                <div>
                  <Label>Reason (Optional)</Label>
                  <Input
                    value={blockForm.reason}
                    onChange={(e) => setBlockForm(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder="Reason for blocking"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <Button
                  onClick={handleBlockPoster}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  data-testid="confirm-block-btn"
                >
                  Block Poster
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowBlockModal(false);
                    setPosterToBlock(null);
                    setBlockForm({ identifier: '', identifier_type: 'phone', reason: '' });
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Unblock Poster Modal */}
        <ConfirmModal
          isOpen={showUnblockModal}
          onClose={() => {
            setShowUnblockModal(false);
            setPosterToUnblock(null);
          }}
          onConfirm={handleUnblockPoster}
          title="Unblock Poster"
          message={`Are you sure you want to unblock "${posterToUnblock?.identifier}"? They will be able to submit photos again.`}
          confirmText="Unblock"
          confirmVariant="default"
        />

        {/* Draw Winner Modal */}
        <ConfirmModal
          isOpen={showDrawWinnerModal}
          onClose={() => setShowDrawWinnerModal(false)}
          onConfirm={handleDrawWinner}
          title="Draw Competition Winner"
          message={`Are you sure you want to manually draw the winner for ${getMonthName(competitionMonth)}? The photo with the most votes will be declared the winner.`}
          confirmText="Draw Winner"
          confirmVariant="default"
        />
      </main>
      
      <Footer />
    </div>
  );
};

export default AdminCompetition;
